import axios from 'axios';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

export interface LeaveRequest {
  id: number;
  userId: number;
  tenantId: number;
  type: 'vacation' | 'permission' | 'sickness' | 'rol';
  startDate: string;
  endDate: string;
  durationType: 'full_day' | 'half_day_morning' | 'half_day_afternoon' | 'hours';
  hours: number | null;
  reason: string | null;
  state: 'draft' | 'pending' | 'approved' | 'rejected' | 'cancelled';
  changeType?: 'none' | 'amendment' | 'cancellation';
  category?: 'original' | 'amendment' | 'cancellation';
  parentRequestId?: number | null;
  rootRequestId?: number;
  reviewedBy: number | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  reviewer?: {
    id: number;
    name: string;
  };
}

export interface CreateLeaveRequestDto {
  type: 'vacation' | 'permission' | 'sickness' | 'rol';
  startDate: string;
  endDate: string;
  durationType: 'full_day' | 'half_day_morning' | 'half_day_afternoon' | 'hours';
  hours?: number;
  reason?: string;
}

export interface RequestLeaveCancellationDto {
  reason?: string;
}

export interface ApprovedHours {
  vacationHours: number;
  permissionHours: number;
}

export const leaveRequestService = {
  // My requests
  getMyRequests: async (): Promise<LeaveRequest[]> => {
    const response = await axios.get(`${API_URL}/leave-requests/my`);
    return response.data;
  },

  // Create
  create: async (dto: CreateLeaveRequestDto): Promise<LeaveRequest> => {
    const response = await axios.post(`${API_URL}/leave-requests`, dto);
    return response.data;
  },

  // Create and submit atomically
  createAndSubmit: async (dto: CreateLeaveRequestDto): Promise<LeaveRequest> => {
    const response = await axios.post(`${API_URL}/leave-requests/create-and-submit`, dto);
    return response.data;
  },

  // Update
  update: async (id: number, dto: Partial<CreateLeaveRequestDto>): Promise<LeaveRequest> => {
    const response = await axios.patch(`${API_URL}/leave-requests/${id}`, dto);
    return response.data;
  },

  // Delete
  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_URL}/leave-requests/${id}`);
  },

  // Submit for approval
  submit: async (id: number): Promise<LeaveRequest> => {
    const response = await axios.post(`${API_URL}/leave-requests/${id}/submit`);
    return response.data;
  },

  // Get pending for approval (Manager/Admin)
  /** `onlyMine` narrows to requests from people whose direct approver is the caller. */
  getPending: async (onlyMine = false): Promise<LeaveRequest[]> => {
    const url = `${API_URL}/leave-requests/pending`;
    const response = onlyMine ? await axios.get(url, { params: { onlyMine: 1 } }) : await axios.get(url);
    return response.data;
  },

  // Reviewed requests (approved / rejected / cancelled) visible to the approver
  /** `onlyMine` narrows to requests from people whose direct approver is the caller. */
  getHistory: async (onlyMine = false): Promise<LeaveRequest[]> => {
    const url = `${API_URL}/leave-requests/history`;
    const response = onlyMine ? await axios.get(url, { params: { onlyMine: 1 } }) : await axios.get(url);
    return response.data;
  },

  // Approve
  approve: async (id: number): Promise<LeaveRequest> => {
    const response = await axios.post(`${API_URL}/leave-requests/${id}/approve`);
    return response.data;
  },

  // Reject
  reject: async (id: number, reason: string): Promise<LeaveRequest> => {
    const response = await axios.post(`${API_URL}/leave-requests/${id}/reject`, { reason });
    return response.data;
  },

  // Cancel (only owner can cancel their pending request)
  cancel: async (id: number): Promise<LeaveRequest> => {
    const response = await axios.post(`${API_URL}/leave-requests/${id}/cancel`);
    return response.data;
  },

  // Request amendment for an approved leave (requires new approval)
  requestAmendment: async (id: number, dto: CreateLeaveRequestDto): Promise<LeaveRequest> => {
    const response = await axios.post(`${API_URL}/leave-requests/${id}/request-amendment`, dto);
    return response.data;
  },

  // Request cancellation for an approved leave (requires new approval)
  requestCancellation: async (id: number, dto?: RequestLeaveCancellationDto): Promise<LeaveRequest> => {
    const response = await axios.post(`${API_URL}/leave-requests/${id}/request-cancellation`, dto ?? {});
    return response.data;
  },

  // Get approved hours for period (for timesheet integration)
  getApprovedHours: async (
    userId: number,
    startDate: string,
    endDate: string,
  ): Promise<ApprovedHours> => {
    const response = await axios.get(`${API_URL}/leave-requests/approved`, {
      params: { userId, startDate, endDate },
    });
    return response.data;
  },

  // Get pending count
  getPendingCount: async (): Promise<{ count: number }> => {
    const pending = await leaveRequestService.getPending();
    return { count: pending.length };
  },
};
